import type { AdminOverview, Deployment, Site, SiteAnalytics, User } from "./types";

export type ApiErrorDetails = {
  title?: string;
  explanation?: string;
  offendingPath?: string;
  suggestion?: string;
  steps?: string[];
  expectedStructure?: string[];
  limits?: Record<string, number | string>;
};

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: ApiErrorDetails,
    public requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const request = async <T>(path: string, options: RequestInit = {}): Promise<T> => {
  const isForm = options.body instanceof FormData;
  const hasBody = options.body !== undefined && options.body !== null;
  const response = await fetch(path, {
    ...options,
    credentials: "include",
    headers: { ...(!isForm && hasBody ? { "Content-Type": "application/json" } : {}), ...options.headers },
  });
  const body = await response.json().catch(() => ({})) as {
    error?: { code?: string; message?: string; details?: ApiErrorDetails; requestId?: string };
  };
  if (!response.ok) {
    throw new ApiError(
      body.error?.code ?? "REQUEST_FAILED",
      body.error?.message ?? "Не удалось выполнить запрос",
      response.status,
      body.error?.details,
      body.error?.requestId,
    );
  }
  return body as T;
};

export const api = {
  me: () => request<{ user: User | null }>("/api/auth/me"),
  login: (email: string, password: string) => request<{ user: User }>("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  register: (name: string, email: string, password: string) => request<{ user: User; requiresVerification: boolean; emailDelivered: boolean; developmentVerificationUrl?: string }>("/api/auth/register", { method: "POST", body: JSON.stringify({ name, email, password }) }),
  resendVerification: (email: string) => request<{ accepted: true; emailDelivered?: boolean; developmentVerificationUrl?: string }>("/api/auth/resend-verification", { method: "POST", body: JSON.stringify({ email }) }),
  logout: () => request<{ success: true }>("/api/auth/logout", { method: "POST", body: "{}" }),
  verifyEmail: (token: string) => request<{ user: User }>("/api/auth/verify-email", { method: "POST", body: JSON.stringify({ token }) }),
  requestReset: (email: string) => request<{ accepted: true; developmentResetUrl?: string }>("/api/auth/request-password-reset", { method: "POST", body: JSON.stringify({ email }) }),
  resetPassword: (token: string, password: string) => request<{ success: true }>("/api/auth/reset-password", { method: "POST", body: JSON.stringify({ token, password }) }),
  sites: () => request<Site[]>("/api/sites"),
  site: (id: string) => request<Site>(`/api/sites/${id}`),
  analytics: (id: string) => request<SiteAnalytics>(`/api/sites/${id}/analytics`),
  adminOverview: () => request<AdminOverview>("/api/admin/overview"),
  subdomainAvailability: (slug: string, siteId?: string) => {
    const query = new URLSearchParams({ slug });
    if (siteId) query.set("siteId", siteId);
    return request<{ slug: string; available: boolean; unchanged: boolean; code: string | null; message: string }>(`/api/sites/subdomain-availability?${query}`);
  },
  createSite: (input: { name: string; slug: string; spaFallback: boolean }) => request<Site>("/api/sites", { method: "POST", body: JSON.stringify(input) }),
  updateSite: (id: string, input: { name?: string; slug?: string; spaFallback?: boolean }) => request<Site>(`/api/sites/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  deleteSite: (id: string) => request<{ success: true }>(`/api/sites/${id}`, { method: "DELETE" }),
  deploy: (id: string, file: File) => { const body = new FormData(); body.append("archive", file); return request<Deployment>(`/api/sites/${id}/deployments`, { method: "POST", body }); },
  activate: (siteId: string, deploymentId: string) => request<Deployment>(`/api/sites/${siteId}/deployments/${deploymentId}/activate`, { method: "POST", body: "{}" }),
  deleteDeployment: (siteId: string, deploymentId: string) => request<{ success: true }>(`/api/sites/${siteId}/deployments/${deploymentId}`, { method: "DELETE" }),
};
